import type {
	IVault,
	IVaultCreated,
	IVaultDetail,
	IVaultDocument,
	IVaultFilters,
	IVaultSource,
	IVaultStatus,
} from './types';

import type { KeyboardEvent } from 'react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useEvaultAddon, useNotification } from 'hooks';
import { ROUTES } from 'routes';
import {
	Blank,
	Kebab,
	PageHead,
	Pager,
	SearchBox,
	StatusChip,
	Tabs,
} from 'views/design-system';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';

import { EvaultAddonToggle } from './addon-toggle';
import { AddonUpsellModal } from './addon-upsell-modal';
import { ArchiveModal } from './archive-modal';
import { CreateVaultModal } from './create-vault-modal';
import { DocumentPanel } from './document-panel';
import { isoOf, toRelativeTime, useIsYou } from './derive';
import { useEvaultApi } from './use-evault-api';
import { useDriveView, useEvaultDeck } from './use-evault-deck';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './evault-deck.scss';

/** Sort options, mapped onto the server's `sort` + `order` pair. */
const SORTS: {
	key: string;
	label: string;
	sort: IVaultFilters['sort'];
	order: IVaultFilters['order'];
}[] = [
	{ key: 'name-asc', label: 'Name A→Z', sort: 'name', order: 'asc' },
	{ key: 'name-desc', label: 'Name Z→A', sort: 'name', order: 'desc' },
	{ key: 'updated', label: 'Last modified', sort: 'updatedAt', order: 'desc' },
	{ key: 'docs', label: 'Most documents', sort: 'documentCount', order: 'desc' },
];

const NO_WRITE = 'You need write access to eVault.';
const NEEDS_ADDON_CREATE = 'Creating vaults needs the eVault add-on.';
const NEEDS_ADDON_TRANSFER = 'Transfers need the eVault add-on.';

/**
 * What the Documents panel hands back up.
 *
 * The drive's bulk bar counts vaults AND documents, so it is drawn here rather
 * than inside the panel — but the document selection, and the transfer modal it
 * opens, both live down there. This is the handle: the selected documents to
 * count, the modal to open, and the clear that actually empties the checkboxes.
 */
interface IDocSelection {
	documents: IVaultDocument[];
	openTransfer: () => void;
	clear: () => void;
}

const NO_DOC_SELECTION: IDocSelection = {
	documents: [],
	openTransfer: () => undefined,
	clear: () => undefined,
};

/**
 * The eVault drive, rebuilt natively on the v2 design system (spec 25, R1).
 *
 * It replaces `views/evault/evault-page.tsx`, which mounted esign.frontend's
 * `<simplici-evault>` web component. That component draws its own light-only
 * chrome inside its own RecoilRoot, so this route was the one redesigned surface
 * admin.panel could neither theme nor reach into — no dark mode, no `PageHead`,
 * no `Drawer`. The endpoints and the scoped token are unchanged; only the render
 * layer is new, and the host page stays on disk, unrouted.
 *
 * Two things the component had in state but never on screen are drawn here: the
 * search box (its `filters.search` reached the validator and both aggregations
 * with no input rendered) and the pager (`page` was hard-coded to 1 from every
 * call site, so a 25th vault was unreachable).
 */
export const EvaultDeck = () => {
	const navigate = useNavigate();
	const api = useEvaultApi();
	const isYou = useIsYou();
	const { successNotification } = useNotification();
	const { checkUserWritePermission } = useUserRoles();
	const [view, setView] = useDriveView();
	const [docSel, setDocSel] = useState<IDocSelection>(NO_DOC_SELECTION);
	/* One fetch, one state, for BOTH `EvaultAddonToggle`s this page can render
	   at once (the head, and again inside the unentitled `Blank`) — see the
	   component's own doc comment. */
	const addon = useEvaultAddon();

	/* The write surfaces. `created` is the one that changes what the page IS
	   rather than what is on top of it (R7): a first-ever vault replaces the
	   drive with a confirmation, because it is an onboarding moment; every later
	   one is a toast over the list it just joined. */
	const [createOpen, setCreateOpen] = useState(false);
	const [renaming, setRenaming] = useState<IVault | null>(null);
	const [archiving, setArchiving] = useState<{
		vault: IVault;
		mode: 'archive' | 'restore';
	} | null>(null);
	const [upsellOpen, setUpsellOpen] = useState(false);
	const [created, setCreated] = useState<IVaultCreated | null>(null);

	const {
		tab,
		chooseTab,
		typedSearch,
		setTypedSearch,
		filters,
		setFilter,
		page,
		setPage,
		pages,
		vaults,
		refetchVaults,
		entitlement,
		selectedVaultIds,
		toggleVault,
		toggleAllVaults,
		clearSelection,
		activeFilterCount,
	} = useEvaultDeck(api);

	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.eVault),
		[checkUserWritePermission]
	);

	/* `false` means the server said no. `null` means the probe never answered,
	   and an unanswered probe is not a refusal — see IEntitlementState. */
	const blocked = entitlement.entitled === false;

	/* Switching the add-on on or off unlocks the drive without a reload: a
	   fresh token, a fresh entitlement probe, a fresh vault list.
	   `entitlement.refetch`/`refetchVaults`, read directly inside `unlock`,
	   would still be the PRE-remint closures — `unlock` itself is fixed at the
	   render before the click, and the new token only exists from the NEXT
	   render on. So `unlock` only remints and flags the request; the effect
	   below, which re-runs on that next render, is what actually calls
	   `refetch`/`refetchVaults` — with closures that already see the new
	   token. Read through refs, not as effect deps: `useEntitlement` returns a
	   fresh object every render, so depending on `entitlement` directly would
	   re-fire this effect (and re-issue both requests) on every unrelated
	   re-render for as long as `unlockPending` stays true. */
	const [unlockPending, setUnlockPending] = useState(false);
	const entitlementRef = useRef(entitlement);
	entitlementRef.current = entitlement;
	const refetchVaultsRef = useRef(refetchVaults);
	refetchVaultsRef.current = refetchVaults;

	const unlock = useCallback(async () => {
		await api.remintToken();
		setUnlockPending(true);
	}, [api]);

	useEffect(() => {
		if (!unlockPending) return;
		void (async () => {
			await entitlementRef.current.refetch();
			await refetchVaultsRef.current();
			setUnlockPending(false);
		})();
	}, [unlockPending]);

	const sortKey = useMemo(
		() =>
			SORTS.find(s => s.sort === filters.sort && s.order === filters.order)?.key ??
			'name-asc',
		[filters.sort, filters.order]
	);

	const chooseSort = useCallback(
		(key: string) => {
			const next = SORTS.find(s => s.key === key) ?? SORTS[0];
			if (!next) return;
			setFilter('sort', next.sort);
			setFilter('order', next.order);
		},
		[setFilter]
	);

	const openVault = useCallback(
		(vault: IVault) => navigate(`/evault/${vault._id}`),
		[navigate]
	);

	/* Disabled WITH the reason on it, never absent: "you need write access" and
	   "this needs the add-on" are different answers, and a button that simply
	   vanished would give neither. */
	const canCreate = canWrite && !blocked;
	const createTitle = !canWrite ? NO_WRITE : blocked ? NEEDS_ADDON_CREATE : undefined;

	/* Rename and archive are NOT entitlement-gated — the server gates create and
	   transfer only, so a lapsed add-on must not lock someone out of tidying the
	   vaults they already own. */
	const writeHint = canWrite ? undefined : NO_WRITE;

	const kebabItems = useCallback(
		(vault: IVault) => [
			{ label: 'Open', onClick: () => openVault(vault) },
			{
				label: 'Rename',
				onClick: () => setRenaming(vault),
				disabled: !canWrite,
				hint: writeHint,
			},
			vault.status === 'archived'
				? {
						label: 'Restore',
						onClick: () => setArchiving({ vault, mode: 'restore' }),
						disabled: !canWrite,
						hint: writeHint,
				  }
				: {
						label: 'Archive',
						onClick: () => setArchiving({ vault, mode: 'archive' }),
						disabled: !canWrite,
						hint: writeHint,
				  },
		],
		[canWrite, openVault, writeHint]
	);

	/* ------------------------------------------------------------- writes */

	const onCreated = useCallback(
		(vault: IVaultCreated) => {
			setCreateOpen(false);
			/* Page 1 whatever the sort: a vault created while the reader is on
			   page 3 of their own drive is a vault they cannot see. */
			setPage(1);
			void refetchVaults();
			if (vault.isFirstVault) {
				setCreated(vault);
				return;
			}
			successNotification(`Vault created — ${vault.name} is ready to use`);
		},
		[refetchVaults, setPage, successNotification]
	);

	const onRenamed = useCallback(
		(vault: IVaultDetail) => {
			setRenaming(null);
			void refetchVaults();
			successNotification(`Vault renamed — ${vault.name}`);
		},
		[refetchVaults, successNotification]
	);

	const onArchived = useCallback(
		(vault: IVaultDetail, status: IVaultStatus) => {
			setArchiving(null);
			clearSelection();
			docSel.clear();
			void refetchVaults();
			successNotification(
				status === 'archived'
					? `Vault archived — ${vault.name} is on the Archived tab`
					: `Vault restored — ${vault.name} is active again`
			);
		},
		[clearSelection, docSel, refetchVaults, successNotification]
	);

	/* Leaving the confirmation is a return to the drive, not a dismissal: the
	   list behind it was refetched when the vault was created, so it already has
	   the new vault with its NEW chip on it. */
	const backToAll = useCallback(() => setCreated(null), []);

	const clearAll = useCallback(() => {
		clearSelection();
		docSel.clear();
	}, [clearSelection, docSel]);

	const documentCount = docSel.documents.length;
	const selectionCount = selectedVaultIds.length + documentCount;

	/* The header checkbox's own state. Indeterminate is deliberately NOT drawn:
	   the invoices table needs it because a partial selection there is a partial
	   CHARGE, and nothing here is irreversible from a checkbox. */
	const allVaultsChecked =
		vaults.items.length > 0 &&
		vaults.items.every(vault => selectedVaultIds.includes(vault._id));

	/* A first-ever drive and a filtered miss are different screens (FR-V2), and
	   a failed load is neither: it keeps the controls and shows the retry band,
	   so "the service is down" is never dressed up as "you have no vaults". */
	const isFirstRun =
		vaults.isLoaded &&
		!vaults.error &&
		vaults.total === 0 &&
		activeFilterCount === 0 &&
		tab === 'active';
	const showUnentitledBlank = entitlement.isLoaded && blocked;
	const showFirstRunBlank = isFirstRun && !blocked;

	return (
		<div className="sv2-scope sv2 evault-deck">
			<PageHead
				title="eVault"
				sub="Encrypted originals of every completed agreement, organised by vault."
				action={
					<>
						{/* The reference for the endpoints behind this page: the v3
						    console, which generates itself from the live contract and
						    already carries eVault's own tag. A `.btn`, not the `.cta`:
						    creating a vault is the one page-level action here, and
						    reading the docs sits beside it rather than competing with
						    it. `?v=v3` pins the version the link promises. */}
						<button
							className="btn"
							type="button"
							onClick={() => navigate(`${ROUTES.API}?v=v3`)}
						>
							API docs
						</button>
						<EvaultAddonToggle
							addon={addon}
							onChanged={unlock}
							canWrite={checkUserWritePermission(PERMISSION_SCREEN.Billing)}
						/>
						{vaults.isLoaded && !created && !blocked ? (
							<button
								className="cta"
								type="button"
								disabled={!canCreate}
								title={createTitle}
								onClick={() => setCreateOpen(true)}
							>
								+ Create vault
							</button>
						) : null}
					</>
				}
			/>

			{/* Hidden behind the first-vault confirmation: that screen is an
			    onboarding moment with one thing to do, and a tab strip over it
			    invites a click that throws it away. */}
			{!created && (
				<Tabs
					tabs={[
						{
							key: 'active',
							label: 'Active',
							count: tab === 'active' ? vaults.total : undefined,
						},
						{ key: 'archived', label: 'Archived' },
					]}
					active={tab}
					onChange={key => chooseTab(key as IVaultStatus)}
				/>
			)}

			{api.vaultTokenError ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<div className="errbar">
						<span className="et">
							{api.vaultTokenError} The eVault service could not start a session.
						</span>
						<button
							className="reinv errbar__act"
							type="button"
							onClick={() => window.location.reload()}
						>
							Try again
						</button>
					</div>
				</div>
			) : !vaults.isLoaded ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<div className="sv2-loading">Loading vaults…</div>
				</div>
			) : created ? (
				/* R7 / D6: the FULL-PANEL confirmation, not the Figma's in-context
				   green banner. A first-ever vault is the moment the feature starts
				   existing for this workspace, and it earns a screen; every later
				   one gets the toast, because by then the drive is the story. */
				<div className="panel created" style={{ marginTop: 12 }}>
					<div className="ctick" aria-hidden="true">
						&#10003;
					</div>
					<div className="ct">Vault created</div>
					<div className="cd">
						{created.name} is ready to use. Documents will arrive as agreements
						vaulted here complete.
					</div>

					<div className="vcard tile cvault">
						<span className="fold" aria-hidden="true">
							&#9707;
						</span>
						<span className="vmeta">
							<span className="nm">{created.name}</span>
							{/* Only when the SERVER says so (C2). `encryptionStandard` is
							    a schema default nobody measures, so it is rendered as the
							    claim it is and omitted entirely when `encrypted` is
							    false — never asserted by this screen. */}
							{!!created.encrypted && (
								<span className="fund">
									Encrypted at rest · {created.encryptionStandard}
								</span>
							)}
						</span>
						<StatusChip verdict="ok">JUST CREATED</StatusChip>
					</div>

					<button
						className="cta"
						type="button"
						onClick={() => navigate(`/evault/${created._id}`)}
					>
						Open vault &rarr;
					</button>
					<div className="cback">
						or{' '}
						<button className="reinv" type="button" onClick={backToAll}>
							back to all vaults
						</button>
					</div>
				</div>
			) : showUnentitledBlank ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<Blank
						title="eVault is not on your plan yet"
						description="Add the eVault service to keep the authoritative original of every signed agreement in an encrypted vault with a tamper-evident audit trail."
						action={
							<EvaultAddonToggle
								addon={addon}
								onChanged={unlock}
								canWrite={checkUserWritePermission(PERMISSION_SCREEN.Billing)}
							/>
						}
					/>
				</div>
			) : showFirstRunBlank ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<Blank
						title="Your eVault is empty"
						description={
							<>
								Create a vault to securely store and organise your signed
								documents. Everything you save here is encrypted at rest and
								accessible only to you and people you invite.
								<span className="bbadges">
									<span className="vch ns">Encrypted at rest</span>
									<span className="vch ns">Organised by vault</span>
									<span className="vch ns">Deposited by signing</span>
								</span>
							</>
						}
						action={
							<button
								className="cta"
								type="button"
								disabled={!canCreate}
								title={createTitle}
								onClick={() => setCreateOpen(true)}
							>
								Create your first vault
							</button>
						}
					/>
				</div>
			) : (
				<>
					<div className="ctl" style={{ marginTop: 12 }}>
						<SearchBox
							value={typedSearch}
							onChange={setTypedSearch}
							placeholder="Search vaults"
							title="Searches vault name"
						/>
						<select
							className="range"
							aria-label="Modified"
							value={filters.modified ?? ''}
							onChange={e =>
								setFilter('modified', (e.target.value || null) as IVaultFilters['modified'])
							}
						>
							<option value="">Any time</option>
							<option value="today">Today</option>
							<option value="7d">Last 7 days</option>
							<option value="30d">Last 30 days</option>
						</select>
						<select
							className="range"
							aria-label="Source"
							value={filters.source ?? ''}
							onChange={e =>
								setFilter('source', (e.target.value || null) as IVaultSource | null)
							}
						>
							<option value="">Any source</option>
							<option value="envelope">Envelope</option>
							<option value="template">Template</option>
							<option value="onboarding">Onboarding</option>
						</select>
						<select
							className="range"
							aria-label="Sort vaults"
							value={sortKey}
							onChange={e => chooseSort(e.target.value)}
						>
							{SORTS.map(s => (
								<option key={s.key} value={s.key}>
									{s.label}
								</option>
							))}
						</select>
						{/* `aria-checked` + role=radio, not `aria-pressed`: that is the
						    pair `.seg` in surface.scss actually styles, and the
						    Appearance card on General Settings uses the same. */}
						<div className="seg" role="radiogroup" aria-label="View">
							<button
								type="button"
								role="radio"
								aria-checked={view === 'list'}
								onClick={() => setView('list')}
							>
								&#8801; List
							</button>
							<button
								type="button"
								role="radio"
								aria-checked={view === 'grid'}
								onClick={() => setView('grid')}
							>
								&#9638; Grid
							</button>
						</div>
						<Pager page={page} pages={pages} onChange={setPage} />
					</div>

					<div className="panel">
						<div className="ph">
							Vaults
							{!vaults.error && <span className="r">{vaults.total}</span>}
						</div>

						{/* §9's sentence verbatim, with the server's own words in the
						    title — the copy is the contract, the detail is the clue. */}
						{vaults.error && (
							<div className="errbar" title={vaults.error}>
								<span className="et">
									Could not load your vaults. The eVault service did not respond —
									this is a connection problem, not missing data.
								</span>
								<button
									className="reinv errbar__act"
									type="button"
									onClick={() => void refetchVaults()}
								>
									Try again
								</button>
							</div>
						)}

						{vaults.isLoading && vaults.items.length === 0 && !vaults.error && (
							<div className="sv2-loading">Loading vaults…</div>
						)}

						{!vaults.isLoading && !vaults.error && vaults.items.length === 0 && (
							<div className="sv2-empty">No vaults match these filters.</div>
						)}

						{vaults.items.length > 0 && view === 'list' && (
							<div className="tscroll">
								<table className="stbl vtbl">
									<thead>
										<tr>
											<th className="ckcol">
												<input
													type="checkbox"
													checked={allVaultsChecked}
													onChange={() =>
														toggleAllVaults(vaults.items.map(v => v._id))
													}
													aria-label="Select all vaults on this page"
												/>
											</th>
											<th>Name</th>
											<th className="num">Documents</th>
											{/* §10 — the schedule every document in the vault is filed
											    under. Beside the count because the two together are what
											    the vault IS: how many records, kept for how long. */}
											<th className="ret">Retention</th>
											<th>Owner</th>
											<th>Updated</th>
											<th>Encrypted</th>
											<th className="accol">Actions</th>
										</tr>
									</thead>
									<tbody>
										{vaults.items.map(vault => {
											const selected = selectedVaultIds.includes(vault._id);
											const mine = isYou(vault.owner?._id);
											/* The row is the primary control in list view, so it takes the
											   same two keys the grid card does: Enter opens the vault, Space
											   picks it WITHOUT navigating — and Space has to be prevented, or
											   the page scrolls out from under the reader as well.

											   `target !== currentTarget` is what makes that safe. The handler
											   is on the <tr>, and the checkbox and the kebab inside it bubble
											   their own keystrokes up here: without the guard, Space on the
											   focused checkbox would have its native toggle cancelled by the
											   preventDefault and then be re-toggled by hand — a net no-change
											   today, and a silent double the day the two stop agreeing. */
											const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
												if (e.target !== e.currentTarget) return;
												if (e.key === 'Enter') openVault(vault);
												if (e.key === ' ') {
													e.preventDefault();
													toggleVault(vault._id);
												}
											};
											return (
												<tr
													key={vault._id}
													className={selected ? 'sel' : ''}
													tabIndex={0}
													onClick={() => openVault(vault)}
													onKeyDown={onRowKey}
												>
													<td
														className="ckcol"
														onClick={e => e.stopPropagation()}
														role="presentation"
													>
														<input
															type="checkbox"
															aria-label={`Select ${vault.name}`}
															checked={selected}
															onChange={() => toggleVault(vault._id)}
														/>
													</td>
													<td>
														<span className="nm">{vault.name}</span>
														{vault.isNew && (
															<StatusChip verdict="strt">NEW</StatusChip>
														)}
													</td>
													<td className="num">{vault.documentCount ?? 0}</td>
													<td className="ret">
														{vault.retentionPolicy ? (
															<span
																title={`${vault.retentionPolicy.scheduleCode} · ${vault.retentionPolicy.governingRule}`}
															>
																{vault.retentionPolicy.category}
																<span className="fund">
																	{' '}
																	·{' '}
																	{vault.retentionPolicy.retentionYears === null
																		? 'permanent'
																		: `${vault.retentionPolicy.retentionYears} yr`}
																</span>
															</span>
														) : (
															/* Says so, rather than a dash a reader could take for
															   "not applicable": a vault with no schedule files every
															   record with none, and that is worth noticing. */
															<span className="fund">No schedule</span>
														)}
													</td>
													{/* A PERSON, so it reads in the sans face; the count above
													    and the relative time below are figures and stay mono. */}
													<td className="own" title={vault.owner?.email}>
														{mine ? 'You' : vault.owner?.name || '—'}
													</td>
													<td className="upd" title={isoOf(vault.updatedAt)}>
														{toRelativeTime(vault.updatedAt) || '—'}
													</td>
													<td>
														{vault.status === 'archived' ? (
															<StatusChip verdict="ns">ARCHIVED</StatusChip>
														) : vault.encrypted ? (
															<span title={vault.encryptionStandard}>
																<StatusChip verdict="ok">ENCRYPTED</StatusChip>
															</span>
														) : (
															'—'
														)}
													</td>
													<td className="accol">
														<Kebab items={kebabItems(vault)} />
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}

						{vaults.items.length > 0 && view === 'grid' && (
							<div className="vgrid">
								{vaults.items.map(vault => {
									const selected = selectedVaultIds.includes(vault._id);
									return (
										<div
											key={vault._id}
											className="vcard tile"
											role="button"
											tabIndex={0}
											aria-pressed={selected}
											onClick={() => openVault(vault)}
											onKeyDown={e => {
												/* The checkbox and the kebab inside the card bubble
												   their own keystrokes up here; only the card's own
												   are the card's to act on. */
												if (e.target !== e.currentTarget) return;
												if (e.key === 'Enter') openVault(vault);
												if (e.key === ' ') {
													e.preventDefault();
													toggleVault(vault._id);
												}
											}}
										>
											<input
												type="checkbox"
												aria-label={`Select ${vault.name}`}
												checked={selected}
												onClick={e => e.stopPropagation()}
												onChange={() => toggleVault(vault._id)}
											/>
											<span className="fold" aria-hidden="true">
												&#9707;
											</span>
											<span className="vmeta">
												<span className="nm">
													{vault.name}
													{vault.isNew && (
														<StatusChip verdict="strt">NEW</StatusChip>
													)}
												</span>
												<span className="fund">
													{vault.documentCount ?? 0} documents · updated{' '}
													{toRelativeTime(vault.updatedAt) || '—'}
												</span>
												<span className="fund">
													{vault.retentionPolicy
														? `${vault.retentionPolicy.category} · ${
																vault.retentionPolicy.retentionYears === null
																	? 'permanent'
																	: `${vault.retentionPolicy.retentionYears} yr`
															}`
														: 'No retention schedule'}
												</span>
											</span>
											<span
												className="vkebab"
												onClick={e => e.stopPropagation()}
												role="presentation"
											>
												<Kebab items={kebabItems(vault)} />
											</span>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{selectionCount > 0 && (
						<div className="bulkbar">
							<span>
								<b>{selectedVaultIds.length}</b>{' '}
								{selectedVaultIds.length === 1 ? 'vault' : 'vaults'} ·{' '}
								<b>{documentCount}</b>{' '}
								{documentCount === 1 ? 'document' : 'documents'} selected
							</span>
							{/* The transferable unit is the DOCUMENT (D16). A vault
							    selection alone must never light this — that is §2.10,
							    where the detail view's counter included vaults and the
							    button enabled with nothing to transfer. */}
							<button
								className="dbtn pri"
								type="button"
								disabled={blocked || documentCount === 0}
								title={blocked ? NEEDS_ADDON_TRANSFER : undefined}
								onClick={docSel.openTransfer}
							>
								Transfer ownership ({documentCount})
							</button>
							<button className="reinv" type="button" onClick={clearAll}>
								Clear
							</button>
						</div>
					)}

					<DocumentPanel
						api={api}
						vaultIds={selectedVaultIds}
						view={view}
						blocked={blocked}
						onSelectionChange={setDocSel}
						transferPrice={addon.status?.transferPrice ?? 0}
						/* For the re-vault modal's picker: the drive holds the whole list
						   already, so the modal needs no request of its own. */
						vaults={vaults.items}
					/>

					<div className="foot">
						Documents cannot be uploaded directly. Every original here carries a
						certificate of completion and a signing audit trail.
					</div>
				</>
			)}

			<CreateVaultModal
				api={api}
				open={createOpen}
				mode="create"
				onClose={() => setCreateOpen(false)}
				onCreated={onCreated}
				/* A 403 here is not a form error: the modal closes and the add-on
				   takes its place (R10). */
				onUnentitled={() => setUpsellOpen(true)}
			/>

			<CreateVaultModal
				api={api}
				open={!!renaming}
				mode="rename"
				vault={renaming}
				onClose={() => setRenaming(null)}
				onRenamed={onRenamed}
			/>

			<ArchiveModal
				api={api}
				open={!!archiving}
				vault={archiving?.vault ?? null}
				mode={archiving?.mode ?? 'archive'}
				onClose={() => setArchiving(null)}
				onDone={onArchived}
			/>

			{/* Mounted only while open: `useEvaultAddon` fetches on mount, and a
			    billing request on every load of the drive would be a request for a
			    screen most visits never see. */}
			{upsellOpen && (
				<AddonUpsellModal open onClose={() => setUpsellOpen(false)} />
			)}
		</div>
	);
};

export default EvaultDeck;
